import { Navigate, useNavigate } from 'react-router-dom';
import { useCaptures } from '../../data/queries/use-captures';
import { ReportCapturePhotoGroup } from './ReportCapturePhotoGroup';
import { useReportBuilder } from './report-builder-state';
import { reportBuilderPath, reportPreviewPath } from './report-route';

/**
 * STEP TWO — choose the photos, and write the one optional Description.
 * M10 / S10-02.
 *
 * **THIS IS A REAL ADDRESS, NOT A VARIABLE**, which is what makes Android's
 * Back button return Brandon to the Capture step instead of throwing him out of
 * the builder. The in-progress report lives in the layout above, so stepping
 * back and forward keeps every choice.
 *
 * **PHOTOS START UNSELECTED, ALWAYS** (PT §17). Selecting a Capture selected
 * none of its photos, and nothing here changes that. There is no AI, no
 * ranking, no "best work" and no provider call.
 *
 * **A CAPTURE MAY CONTRIBUTE ZERO PHOTOS** (Jerry's decision 2). It is not
 * dropped, it does not block Continue, and no warning pretends the report is
 * invalid. The screen simply shows the truth about what evidence exists and
 * what he chose.
 *
 * **SELECTING A PHOTO IS NOT TOUCHING THE PHOTO.** Nothing on this screen can
 * remove evidence, reclassify it or write Capture history — the Remove and
 * Before/After controls live on the Capture screen, which is deliberately not
 * reused here.
 */
export function ReportPhotoStep() {
  const builder = useReportBuilder();
  const navigate = useNavigate();
  const { status, captures } = useCaptures(builder.projectId);

  /**
   * **ARRIVING HERE WITH NOTHING SELECTED IS A DEAD END, SO IT IS NOT SHOWN.**
   * A refresh, or a link straight to this address, starts a fresh builder with
   * no Captures — there is no saved report to recover, because saving is
   * S10-05. Sending him to the step where the work starts is the truthful
   * answer; **recovering the old report would be persistence, and inventing it
   * here is exactly what this slice must not do.**
   */
  if (builder.captureIds.size === 0) {
    return <Navigate to={reportBuilderPath(builder.projectId)} replace />;
  }

  const selected = captures.filter(({ capture }) => builder.captureIds.has(capture.id));

  return (
    <main className="cl-screen report-screen">
      <p className="project-home-back">
        <button
          type="button"
          className="cl-button cl-button--quiet report-back-button"
          onClick={() => {
            void navigate(-1);
          }}
        >
          ‹ Captures
        </button>
      </p>

      <h1 className="cl-screen-title">Choose photos</h1>
      <p className="report-lead">
        Nothing is chosen for you. Tap a photo to see it large before you decide.
      </p>

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading Captures…
        </p>
      ) : null}

      {selected.map(({ capture, organization }) => (
        <ReportCapturePhotoGroup
          key={capture.id}
          capture={capture}
          locationDisplay={organization?.location_display ?? null}
        />
      ))}

      <section className="report-description" aria-labelledby="report-description-heading">
        <h2 id="report-description-heading" className="sheet-label">
          Description
        </h2>
        {/* **OPTIONAL, AND IT SAYS SO** (Jerry's decision 1). Blank is valid and
            blocks nothing. One Description for the whole report — never one per
            Capture, never AI-written, never a rich-text editor. It belongs to
            this report and writes NOTHING to the Capture's transcript, caption,
            location, status or history. */}
        <p className="report-lead">Optional. One short note covering the whole report.</p>
        <textarea
          id="report-description"
          className="cl-input report-description-input"
          value={builder.description}
          onChange={(event) => {
            builder.setDescription(event.target.value);
          }}
          rows={4}
          aria-label="Report description"
          placeholder="Anything the client should know"
        />
      </section>

      {/* THE PREVIEW IS REAL AS OF S10-03B. Branding is still not editable
          here — it is set once in Settings and this step never asks for it. */}
      <button
        type="button"
        className="cl-button report-continue"
        onClick={() => {
          void navigate(reportPreviewPath(builder.projectId));
        }}
      >
        Preview report
      </button>
    </main>
  );
}
